import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import type {
  SuperpowerSlideBackgroundProps,
  SuperpowerSlideBackgroundRef,
} from './SuperpowerSlideBackground.data';
import styles from './SuperpowerSlideBackground.module.scss';
import {
  scrollInTimeline,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './SuperpowerSlideBackground.transitions';
import Heading from '../../../atoms/heading/Heading';

export default function SuperpowerSlideBackground({
  blok,
  ref,
  ...props
}: SuperpowerSlideBackgroundProps & { ref?: Ref<SuperpowerSlideBackgroundRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<SuperpowerSlideBackgroundRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleHolderRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const hasTransitionedIn = useRef<boolean>(false);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        titleRef,
        subtitleRef,
        backgroundRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const scrollTimeline = useRef(useSetupTimeline(scrollInTimeline, { titleHolderRef }));

  const setProgress = useCallback(
    (progress: number, slideIndex: number) => {
      if (slideIndex === props.slideIndex) {
        // scrollTimeline.current.progress(reMap(progress, 0, 100, 0, 110));
        scrollTimeline.current.progress(progress);
      }

      if (progress > 0 && progress < 1 && !hasTransitionedIn.current) {
        hasTransitionedIn.current = true;
        transitionController.transitionIn();
      } else if ((progress <= 0 || progress >= 1) && hasTransitionedIn.current) {
        hasTransitionedIn.current = false;
        transitionController.transitionOut();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.slideIndex],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      elementRef,
    }),
    [setProgress, elementRef],
  );

  return (
    <div
      ref={elementRef}
      style={
        {
          '--foreground-color': props.config.foregroundColor,
        } as React.CSSProperties
      }
      className={classNames(styles.superpowerSlideBackground, props.className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      <div
        style={
          {
            '--background-color': props.config.backgroundColor,
          } as React.CSSProperties
        }
        ref={backgroundRef}
        className={classNames(styles.background, 'abs-fill')}
      />
      <div ref={titleHolderRef} className={classNames(styles.titleContent)} id="superpower-title">
        <Heading
          ref={titleRef}
          as="h2"
          size="heading-01"
          className={classNames(styles.title)}
          disableTriggerInOnScroll
          transitionOutType="splitCharsSlideOutUp"
          transitionOnMount
        >
          {blok.title?.split(' ').map((str) => (
            <span key={str} className={classNames(styles.line)}>
              {str}
            </span>
          ))}
        </Heading>
      </div>
    </div>
  );
}
